/**
 * Affix
 * 基于 bootstrap3 的 affix 插件的 VueJS 变种
 * 同时增加了更高级的能力：
 *  1. 支持指定`container`，基于`container`来决定状态
 *  2. 支持垫片填充
 * 参考：https://getbootstrap.com/docs/3.3/javascript/#affix
 */

import $ from 'jquery'
// import Vue from 'vue'
import { getFirstComponentChild } from '../../core/utils/KVdom'
import KUuid from '../../core/utils/KUuid'
import { sub, unSub, VIEWPORT_SCROLL } from '../../core/utils/KGevent'
import raf from 'raf'
import { document } from 'ssr-window'

const Config = {
  DEFAULTS: {
    offset: 0,
    target: document,
  },
  RESET: 'km-affix km-affix-top km-affix-bottom',
}

/**
 * @param element 固钉元素
 * @param options
 *   target :
 */
export default {
  name: 'KAffix',
  props: {
    // 触发器，通过改变trigger主动触发affix的计算
    trigger: {
      type: Boolean,
      default: false,
    },
    // 所在容器，affix元素不会超出容器 (selector|node|jQuery element)
    container: {
      validator(value) {
        const type = typeof value
        return type === 'string' || type === 'object'
      },
      default: null,
    },
    reverse: {
      // 反转，only effective on container is specified
      type: Boolean,
      default: false,
    },
    offsetTop: {
      type: Number,
      default: null,
    },
    offsetBottom: {
      type: Number,
      default: null,
    },
    // 滚动监听元素 (selector|node|jQuery element)
    target: {
      once: true,
      validator(value) {
        const type = typeof value
        return type === 'string' || type === 'object'
      },
      // `document`
      default() {
        return Config.DEFAULTS.target
      },
    },
    // 镜像填充，当元素从 affix-top 到 affix 状态时，在元素的位置填充一个等高的元素
    mirror: {
      type: Boolean,
      default: false,
    },
    // 临时禁用
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      $element: null,
      affixed: null,
      affixElement: null, // 特殊用法，通过设置该值，使得当前的 affix 配置作用在 affixElement 元素上
    }
  },
  computed: {
    $container() {
      if (this.container) {
        return $(this.container)
      }
      return null
    },
    $target() {
      return $(this.target)
    },
    id() {
      return KUuid()
    },
  },
  watch: {
    trigger() {
      this.checkPosition()
    },
    offsetTop() {
      this.checkPosition()
    },
    offsetBottom() {
      this.checkPosition()
    },
    affixed(value) {
      if (value === false) {
        this.$emit('affixed')
      } else if (value === 'bottom') {
        this.$emit('un-affixed')
        this.$emit('affixed-bottom')
      } else {
        this.$emit('un-affixed')
        this.$emit('affixed-top')
      }
    },
    disabled(disabled) {
      if (disabled) {
        this.reset()
      } else {
        this.checkPositionWithEventLoop()
      }
    },
    affixElement(value) {
      if (value) {
        this.$element = $(value)
      } else {
        const child = getFirstComponentChild(this.$slots.default)
        this.$element = $(child.elm)
      }
    },
  },
  created() {
  },
  mounted() {
    if (this.target === Config.DEFAULTS.target) {
      sub(VIEWPORT_SCROLL, this.checkPosition)
    } else {
      this.$target.on('scroll.kamala.affix.data-api', this.checkPosition)
      // addPassiveEvent(this.$target[0], 'scroll', this.checkPosition)
    }
    this.$target.on('click.kamala.affix.data-api', this.checkPositionWithEventLoop)
    if (this.affixElement) {
      this.$element = $(this.affixElement)
    } else {
      const child = getFirstComponentChild(this.$slots.default)
      this.$element = $(child.elm)
    }
    this.affixed = null
    this.unpin = null
    this.pinnedOffset = null

    this.checkPosition()
  },
  destroyed() {
    if (this.target === Config.DEFAULTS.target) {
      unSub(VIEWPORT_SCROLL, this.checkPosition)
    } else {
      this.$target.off('scroll.kamala.affix.data-api', this.checkPosition)
    }
    this.$target.off('click.kamala.affix.data-api', this.checkPositionWithEventLoop)
  },
  render() {
    if (this.affixElement) {
      return null
    }
    const child = getFirstComponentChild(this.$slots.default)
    return child
  },
  methods: {
    apiSetAffixElement(element) {
      this.affixElement = element
    },
    reset() {
      this.$element.removeClass(Config.RESET)
      this.affixed = null
      this.unpin = null
      this.pinnedOffset = null
      this.hideMirror()
    },
    hideMirror() {
      raf(() => {
        this.$mirror && this.$mirror.hide()
      })
    },
    showMirror() {
      if (!this.$mirror) {
        this.$mirror = $('<div>').attr('id', `affix-mirror-${this.id}`)
        this.$mirror.insertAfter(this.$element)
        // const $el = $(this.$el)
        const $el = this.$element
        const mirrorCss = {
          width: $el.outerWidth(),
          height: $el.outerHeight(),
          marginTop: $el.css('marginTop'),
          marginLeft: $el.css('marginLeft'),
          marginBottom: $el.css('marginBottom'),
          marginRight: $el.css('marginRight'),
          float: $el.css('float'),
        }
        this.$mirror.css(mirrorCss)
      }
      this.$mirror.show()
    },
    emitBeforeEvent(value) {
      if (value === false) {
        this.showMirror()
        this.$emit('before-affix')
      } else if (value === 'bottom') {
        // this.hideMirror()
        this.$emit('before-un-affix')
        this.$emit('before-affix-bottom')
      } else {
        this.hideMirror()
        this.$emit('before-un-affix')
        this.$emit('before-affix-top')
      }
    },
    getPinnedOffset() {
      if (this.pinnedOffset) {
        return this.pinnedOffset
      }
      this.$element.removeClass(Config.RESET).addClass('km-affix')
      const scrollTop = this.$target.scrollTop()
      const position = this.$element.offset()
      return (this.pinnedOffset = position.top - scrollTop)
    },
    isContainerInView() {
      const scrollTop = this.$target.scrollTop()
      const containerOffset = this.$container.offset()
      const windowHeight = document.documentElement.clientHeight

      const isContainerBelowView = scrollTop + windowHeight <= containerOffset.top
      const isContainerAboveView = containerOffset.top + this.$container.outerHeight() <= scrollTop

      return !isContainerBelowView && !isContainerAboveView
    },
    getReverseState(scrollHeight, height) {
      const offsetTop = this.offsetTop
      const offsetBottom = this.offsetBottom || 0 // reverse 场景 offsetBottom 必须有值
      const scrollTop = this.$target.scrollTop()
      const containerOffset = this.$container.offset()
      const containerHeight = this.$container.outerHeight()
      const clientHeight = document.documentElement.clientHeight

      if (scrollTop + clientHeight <= containerOffset.top) {
        return 'top'
      }

      if (
        scrollTop + clientHeight > containerOffset.top &&
        scrollTop + clientHeight < containerOffset.top + height + offsetBottom
      ) {
        return 'bottom'
      }

      if (scrollTop + clientHeight >= containerOffset.top + containerHeight + offsetTop) {
        return 'top'
      }

      return false
    },
    getContainerState(scrollHeight, height) {
      const offsetTop = this.offsetTop || 0 // container 场景 offsetTop 和 offsetBottom 必须有值
      const offsetBottom = this.offsetBottom || 0
      const scrollTop = this.$target.scrollTop()
      const containerOffset = this.$container.offset()
      const containerHeight = this.$container.outerHeight()

      if (scrollTop <= containerOffset.top - offsetTop) {
        return 'top'
      }

      if (
        scrollTop > containerOffset.top + containerHeight - height - offsetBottom - offsetTop &&
        scrollTop < containerOffset.top + containerHeight
      ) {
        return 'bottom'
      }

      if (scrollTop >= containerHeight.top + containerHeight) {
        return 'top'
      }

      return false
    },
    getState(scrollHeight, height) {
      const offsetTop = this.offsetTop
      const offsetBottom = this.offsetBottom
      const scrollTop = this.$target.scrollTop()
      const position = this.$element.offset()
      const targetHeight = this.$target.height()

      if (offsetTop !== null && this.affixed === 'top') {
        return scrollTop < offsetTop ? 'top' : false
      }

      if (this.affixed === 'bottom') {
        if (offsetTop !== null) {
          return scrollTop + this.unpin <= position.top ? false : 'bottom'
        }
        return scrollTop + targetHeight <= scrollHeight - offsetBottom ? false : 'bottom'
      }

      const initializing = this.affixed == null
      const colliderTop = initializing ? scrollTop : position.top
      const colliderHeight = initializing ? targetHeight : height

      if (offsetTop !== null && scrollTop <= offsetTop) {
        return 'top'
      }

      if (offsetBottom !== null && colliderTop + colliderHeight >= scrollHeight - offsetBottom) {
        return 'bottom'
      }

      return false
    },
    getAffix(scrollHeight, height) {
      // no container
      if (!this.container) {
        return this.getState(scrollHeight, height)
      }

      // container and reverse
      if (this.reverse) {
        return this.getReverseState(scrollHeight, height)
      }

      // container only
      return this.getContainerState(scrollHeight, height)
    },
    checkPosition() {
      if (this.disabled) {
        return
      }

      if (!this.$element.is(':visible')) {
        return
      }

      if (this.container === null && this.offsetTop === null && this.offsetBottom === null) {
        return
      }

      if (this.container && !this.isContainerInView()) {
        this.reset()
        return
      }

      const height = this.$element.outerHeight()
      const scrollHeight = Math.max($(document).height(), $(document.body).height())

      const affix = this.getAffix(scrollHeight, height)

      if (this.affixed !== affix) {
        if (this.unpin != null) this.$element.css('top', '')

        const affixType = 'km-affix' + (affix ? '-' + affix : '')
        this.emitBeforeEvent(affix)

        this.affixed = affix
        this.unpin = affix === 'bottom' ? this.getPinnedOffset() : null

        this.$element.removeClass(Config.RESET).addClass(affixType)
      }

      if (!this.container && affix === 'bottom') {
        this.$element.offset({
          top: scrollHeight - height - this.offsetBottom,
        })
      }
    },
    checkPositionWithEventLoop() {
      setTimeout(this.checkPosition, 1)
    },
  },
}
